// Copyright 2025, University of Maryland, All Rights Reserved

import React from 'react'
import { cn } from '@/components/ui'
import { CopyButton } from './CopyButton'

export interface ClauseTextBoxProps {
  /** The clause text to display */
  text: string
  /** Label shown above the text (optional) */
  label?: string
  /** Visual variant */
  variant?: 'current' | 'recommended' | 'neutral'
  /** Whether to show the copy button */
  showCopy?: boolean
  /** Additional className */
  className?: string
}

const variantClasses = {
  current: 'border-border bg-muted/50',
  recommended: 'border-border bg-muted/50',
  neutral: 'border-border bg-muted/50',
}

/**
 * Styled text box for displaying contract clauses with optional copy functionality.
 */
export const ClauseTextBox: React.FC<ClauseTextBoxProps> = ({
  text,
  label,
  variant = 'neutral',
  showCopy = true,
  className,
}) => {
  if (!text) return null

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border px-5 py-4',
        variantClasses[variant],
        className
      )}
    >
      {(label || showCopy) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
          )}
          {showCopy && <CopyButton text={text} />}
        </div>
      )}
      <p className="text-sm leading-[1.7] whitespace-pre-wrap text-foreground">
        {text}
      </p>
    </div>
  )
}
